/**
 * Accessibility focus management for the SlideCart HQ slide-out cart.
 *
 * The cart is rendered/controlled by a third-party app (#slidecarthq) that we
 * cannot edit directly. This script observes the drawer's `open` class and adds
 * the keyboard/screen-reader behaviour required by WCAG 2.4.3 (Focus Order) and
 * 2.1.2 (No Keyboard Trap):
 *   - keeps the CLOSED drawer out of the tab order / hidden from AT, so tabbing
 *     through the page can't land on its off-screen controls and reveal it;
 *   - moves focus into the drawer when it opens (announces the dialog);
 *   - traps Tab / Shift+Tab inside the drawer while it is open;
 *   - closes the drawer on Escape;
 *   - returns focus to the element that opened it when it closes.
 */
(function () {
  'use strict';

  var WRAPPER_SELECTOR = '#slidecarthq';
  var DRAWER_SELECTOR = '.slidecarthq';
  var OPEN_CLASS = 'open';
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var lastTrigger = null;
  var isOpen = false;

  function getDrawer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  // Take the closed cart out of the tab order and hide it from screen readers so
  // nothing can focus its off-screen controls (drawer buttons + the overlay's
  // tabindex="0"), which would otherwise reveal the cart. Applied to the whole
  // #slidecarthq wrapper so both the overlay and the drawer are covered.
  function setInert(drawer, inert) {
    var wrapper = drawer.closest(WRAPPER_SELECTOR) || drawer;
    if (inert) {
      wrapper.setAttribute('inert', '');
      wrapper.setAttribute('aria-hidden', 'true');
    } else {
      wrapper.removeAttribute('inert');
      wrapper.removeAttribute('aria-hidden');
    }
  }

  function getFocusable(container) {
    return Array.prototype.slice
      .call(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) {
        // Skip elements that are hidden (e.g. collapsed empty-cart upsells).
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
  }

  function closeDrawer(drawer) {
    // Let the app run its own close logic so its internal state stays in sync.
    var closeBtn = drawer.querySelector('header button[aria-label="close cart"]') ||
      drawer.querySelector('header button');
    if (closeBtn) {
      closeBtn.click();
    } else if (typeof window.SLIDECART_CLOSE === 'function') {
      window.SLIDECART_CLOSE();
    }
  }

  function onKeydown(e) {
    var drawer = getDrawer();
    if (!drawer || !drawer.classList.contains(OPEN_CLASS)) return;

    if (e.key === 'Escape' || e.keyCode === 27) {
      e.preventDefault();
      closeDrawer(drawer);
      return;
    }

    if (e.key !== 'Tab' && e.keyCode !== 9) return;

    var focusable = getFocusable(drawer);
    if (!focusable.length) {
      e.preventDefault();
      drawer.focus();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (e.shiftKey) {
      if (active === first || !drawer.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || !drawer.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  }

  function openDrawer(drawer) {
    setInert(drawer, false);
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    if (!drawer.getAttribute('aria-label')) {
      drawer.setAttribute('aria-label', 'Shopping cart');
    }
    drawer.setAttribute('tabindex', '-1');

    // Remember what to return focus to (the Add-to-cart / cart-icon trigger).
    lastTrigger = document.activeElement;

    // Wait a tick for the app to finish rendering the drawer contents, then
    // move focus to the dialog container so screen readers announce it.
    window.setTimeout(function () {
      if (!drawer.classList.contains(OPEN_CLASS)) return;
      drawer.focus();
    }, 100);

    document.addEventListener('keydown', onKeydown, true);
  }

  function handleState() {
    var drawer = getDrawer();
    if (!drawer) return;

    var nowOpen = drawer.classList.contains(OPEN_CLASS);

    if (nowOpen && !isOpen) {
      isOpen = true;
      openDrawer(drawer);
    } else if (!nowOpen && isOpen) {
      isOpen = false;
      document.removeEventListener('keydown', onKeydown, true);
      setInert(drawer, true);

      if (lastTrigger && typeof lastTrigger.focus === 'function' && document.contains(lastTrigger)) {
        lastTrigger.focus();
      }
      lastTrigger = null;
    } else if (!nowOpen && !isOpen) {
      // Initial (or app-rebuilt) closed drawer: keep it off the tab order.
      var wrapper = drawer.closest(WRAPPER_SELECTOR) || drawer;
      if (!wrapper.hasAttribute('inert')) setInert(drawer, true);
    }
  }

  var observer = new MutationObserver(handleState);

  function attach() {
    // Observe the cart wrapper if it exists, otherwise the whole document until
    // the app injects it. Class-toggle checks are cheap so this stays light.
    var target = document.querySelector(WRAPPER_SELECTOR) || document.documentElement;
    observer.observe(target, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    handleState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
